/**
 * Centralized API Client with Automatic Token Refresh
 * 
 * This module provides a unified API client that handles:
 * - Automatic token refresh on 401 responses
 * - Token storage and management
 * - Request retry logic after token refresh
 * - Consistent error handling across all API calls
 */

import { authService } from './auth';
import { debugLogger } from '@/lib/debug-logger';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class ApiClient {
  private static instance: ApiClient | null = null;
  
  private constructor() {}
  
  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Make an authenticated API request with automatic token refresh
   * 
   * This method:
   * 1. Adds authentication headers to the request
   * 2. Automatically refreshes the token on 401 responses
   * 3. Retries the request after successful token refresh
   * 4. Redirects to login on permanent auth failure
   */
  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // API request initiated

    // Make the initial request
    const makeRequest = async () => {
      const token = authService.getToken();
      // Token validation
      const headers = new Headers(options.headers);
      
      // Add auth header if we have a token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      // Always set content-type for JSON requests
      if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
      }
      
      const fullUrl = `${API_BASE_URL}${url}`;
      // Making API request
      
      return fetch(fullUrl, {
        ...options,
        headers,
      });
    };

    let response = await makeRequest();
    // Response received

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401) {
      // Token refresh needed
      debugLogger.warning('API', 'Token expired, attempting refresh', { url });
      const refreshed = await authService.refreshAccessToken();
      
      if (refreshed) {
        // Token refreshed, retrying request
        // Token refreshed successfully
        // Token refreshed successfully, retry the request
        response = await makeRequest();
        // Request retry completed
        // Retry complete
      } else {
        // Authentication failed, redirecting to login
        debugLogger.error('API', 'Token refresh failed', { url });
        // Refresh failed, redirect to login
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication failed. Please log in again.');
      }
    }
    
    return response;
  }

  /**
   * GET request helper
   */
  async get(url: string): Promise<Response> {
    return this.fetch(url, { method: 'GET' });
  }

  /**
   * POST request helper
   */
  async post(url: string, data?: any): Promise<Response> {
    return this.fetch(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request helper
   */
  async put(url: string, data?: any): Promise<Response> {
    return this.fetch(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request helper
   */
  async patch(url: string, data?: any): Promise<Response> {
    return this.fetch(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request helper
   */
  async delete(url: string): Promise<Response> {
    return this.fetch(url, { method: 'DELETE' });
  }

  /**
   * Handle API response and extract data
   */
  async handleResponse<T>(response: Response): Promise<T> {
    // Processing API response
    
    if (!response.ok) {
      // API request failed
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      // Log error details
      debugLogger.error('API', 'Request failed', { status: response.status, message: errorData?.error || errorData?.message });
      throw new Error(errorData.error || errorData.message || 'Request failed');
    }
    
    const data = await response.json();
    // API response processed successfully
    return data;
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();